'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import {
  Menu,
  X,
  Heart,
  Youtube,
  Twitch,
  MessageSquare,
  ChevronRight,
  Radio,
  ExternalLink,
} from 'lucide-react'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isLivePopoverOpen, setIsLivePopoverOpen] = useState(false)
  const [isLiveBlockVisible, setIsLiveBlockVisible] = useState(false)
  const livePopoverRef = useRef<HTMLDivElement>(null)
  const liveButtonRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()
  const router = useRouter()
  const isHome = pathname === '/'

  // Live streaming links
  const liveStreamLinks = [
    {
      name: 'YouTube',
      url: 'https://youtube.com/live/lcb0nPZKII0',
      icon: <Youtube className="w-5 h-5" />,
      color: 'bg-red-600',
    },
    {
      name: 'RuTube',
      url: 'https://rutube.ru/video/443ca7ba391b4e0ea536c764e157fe62/',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-9.5v6l5-3z" />
        </svg>
      ),
      color: 'bg-blue-900',
    },
    {
      name: 'Twitch',
      url: 'http://twitch.tv/nuvertal_dnb',
      icon: <Twitch className="w-5 h-5" />,
      color: 'bg-purple-600',
    },
    {
      name: 'VK Видео Live',
      url: 'http://live.vkplay.ru/nuvertal',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5"
        >
          <path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14c5.6 0 6.93-1.33 6.93-6.93V8.93C22 3.33 20.67 2 15.07 2z" />
          <path d="M12 8.5v7M15.5 12h-7" />
        </svg>
      ),
      color: 'bg-blue-700',
    },
    {
      name: 'VK',
      url: 'https://vk.com/nuvertal_music',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5"
        >
          <path d="M21.579 6.855c.14-.465 0-.806-.662-.806h-2.193c-.558 0-.813.295-.953.619 0 0-1.115 2.719-2.695 4.482-.51.513-.743.675-1.021.675-.139 0-.341-.162-.341-.627V6.855c0-.558-.161-.806-.626-.806H9.642c-.348 0-.558.258-.558.504 0 .528.79.65.87 2.138v3.228c0 .707-.127.836-.407.836-.743 0-2.551-2.729-3.624-5.853-.209-.607-.42-.852-.98-.852H2.752c-.627 0-.752.295-.752.619 0 .582.743 3.462 3.461 7.271 1.812 2.601 4.363 4.011 6.687 4.011 1.393 0 1.565-.313 1.565-.853v-1.966c0-.626.133-.752.574-.752.324 0 .882.164 2.183 1.417 1.486 1.486 1.732 2.153 2.567 2.153h2.192c.626 0 .939-.313.759-.932-.197-.615-.907-1.51-1.849-2.569-.512-.604-1.277-1.254-1.51-1.579-.325-.419-.231-.604 0-.976.001 0 2.672-3.761 2.95-5.04z" />
        </svg>
      ),
      color: 'bg-blue-600',
    },
  ]

  // Close popover when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        livePopoverRef.current &&
        !livePopoverRef.current.contains(event.target as Node) &&
        liveButtonRef.current &&
        !liveButtonRef.current.contains(event.target as Node)
      ) {
        setIsLivePopoverOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Detect scroll to add subtle background on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Prevent body scrolling when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  const scrollToTop = () => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      router.push('/')
    }
    if (isMenuOpen) setIsMenuOpen(false)
  }

  const scrollToSection = (id: string) => {
    if (isHome) {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      router.push(`/#${id}`)
    }
    if (isMenuOpen) {
      setIsMenuOpen(false)
    }
  }

  return (
    <header
      className={`backdrop-blur-xl h-[76px] border-b border-radio-blue/10 fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/60 shadow-md shadow-black/20' : 'bg-black/40'
      }`}
      style={{ backdropFilter: 'blur(16px)' }}
    >
      <div className="container mx-auto flex items-center justify-between py-4 px-4">
        {/* Logo */}
        <button onClick={scrollToTop} className="flex items-center">
          <div className="relative w-24 h-[46px]">
            <Image
              src="/assets/img/logo.png"
              alt="Логотип Радио Саня"
              width={96}
              height={50}
              className="object-contain"
              priority
            />
          </div>
        </button>

        {/* Navigation Menu - Now on the left side */}
        <nav className="hidden md:block flex-grow ml-8">
          <ul className="flex space-x-8" role="menubar">
            {[
              {
                name: 'ГЛАВНАЯ',
                action: (e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault()
                  scrollToTop()
                },
                href: '/',
              },
              {
                name: 'ПОДДЕРЖКА',
                action: (e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault()
                  scrollToSection('support')
                },
                href: '/#support',
                icon: <Heart className="w-4 h-4" />,
              },
              {
                name: 'КОНТАКТЫ',
                action: (e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault()
                  scrollToSection('contacts')
                },
                href: '/#contacts',
              },
            ].map((item) => (
              <li key={item.name}>
                <a
                  href={item.href}
                  onClick={item.action}
                  role="menuitem"
                  className="font-menu text-lg tracking-wider hover:text-radio-blue transition-colors flex items-center"
                >
                  {item.icon && <span className="mr-1 text-radio-blue">{item.icon}</span>}
                  {item.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Stream On Demand - Right side */}
        <a
          href="/strim-na-zakaz"
          className="hidden md:flex items-center gap-1.5 font-menu text-lg tracking-wider hover:text-radio-blue transition-colors ml-auto mr-4"
        >
          <span className="text-radio-blue"><Radio className="w-4 h-4" /></span>
          СТРИМ НА ЗАКАЗ
        </a>

        {/* Live On Air Component - Desktop */}
        {isLiveBlockVisible && (
          <div className="hidden md:flex items-center mr-6 ml-8 relative">
            <div
              ref={liveButtonRef}
              className="flex items-center bg-black/40 border border-radio-blue/30 rounded-full px-3 py-1.5 shadow-[0_0_10px_rgba(30,174,215,0.2)] cursor-pointer hover:bg-black/50 transition-colors group"
              onClick={() => setIsLivePopoverOpen(!isLivePopoverOpen)}
            >
              <div className="relative mr-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <div className="absolute -inset-1 bg-red-500/30 rounded-full animate-ping"></div>
              </div>
              <span className="text-white text-sm font-medium mr-1">В эфире</span>
              <span className="text-radio-blue text-sm font-bold truncate max-w-[120px]"></span>
              <ChevronRight
                className={`w-4 h-4 ml-1 text-gray-400 transition-transform duration-300 ${isLivePopoverOpen ? 'rotate-90' : 'group-hover:translate-x-0.5'}`}
              />
            </div>

            {/* Live Popover */}
            {isLivePopoverOpen && (
              <div
                ref={livePopoverRef}
                className="absolute top-full left-0 mt-2 w-72 bg-black/90 backdrop-blur-md border border-radio-blue/20 rounded-lg shadow-lg shadow-black/50 z-50 overflow-hidden"
              >
                <div className="p-4 border-b border-radio-blue/20">
                  <div className="flex items-center">
                    <Radio className="w-5 h-5 text-radio-blue mr-2" />
                    <h3 className="text-white font-medium">Прямые трансляции</h3>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    Смотрите и слушайте эфиры в реальном времени
                  </p>
                </div>
                <div className="p-3">
                  {liveStreamLinks.map((link, index) => (
                    <Link
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-2 hover:bg-white/5 rounded-md transition-colors group"
                      onClick={() => setIsLivePopoverOpen(false)}
                    >
                      <div
                        className={`w-8 h-8 rounded-full ${link.color} flex items-center justify-center mr-3`}
                      >
                        {link.icon}
                      </div>
                      <div className="flex-grow">
                        <span className="text-white text-sm">{link.name}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-radio-blue transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mobile Header Right Side - Live Component + Menu Button */}
        <div className="md:hidden flex items-center space-x-3">
          {/* Live On Air Component - Mobile */}
          {isLiveBlockVisible && (
            <div
              className="flex items-center bg-black/40 border border-radio-blue/30 rounded-full px-2 py-1 shadow-[0_0_10px_rgba(30,174,215,0.2)] cursor-pointer hover:bg-black/50 transition-colors"
              onClick={() => setIsLivePopoverOpen(!isLivePopoverOpen)}
            >
              <div className="relative mr-1.5">
                <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></div>
                <div className="absolute -inset-1 bg-red-500/30 rounded-full animate-ping"></div>
              </div>
              <span className="text-white text-xs font-medium">В эфире</span>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="p-2 text-white hover:text-radio-blue transition-colors"
            onClick={() => setIsMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Live Popover */}
        {isLivePopoverOpen && isLiveBlockVisible && (
          <div
            className={`absolute top-[76px] left-0 right-0 backdrop-blur-xl border-b border-radio-blue/10 shadow-lg shadow-black/50 z-40 md:hidden transition-all duration-300 ${
              isScrolled ? 'bg-black/60' : 'bg-black/40'
            }`}
          >
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Radio className="w-4 h-4 text-radio-blue mr-2" />
                  <h3 className="text-white text-sm font-medium">Прямые трансляции</h3>
                </div>
                <button
                  onClick={() => setIsLivePopoverOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {liveStreamLinks.map((link, index) => (
                  <Link
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-2 hover:bg-white/5 rounded-md transition-colors"
                    onClick={() => setIsLivePopoverOpen(false)}
                  >
                    <div
                      className={`w-6 h-6 rounded-full ${link.color} flex items-center justify-center mr-2`}
                    >
                      {link.icon}
                    </div>
                    <span className="text-white text-xs">{link.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Menu Offcanvas - Enhanced with Background */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-500 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => {
          // Only close if clicking the outer container, not its children
          if (e.target === e.currentTarget) {
            setIsMenuOpen(false)
          }
        }}
      >
        {/* Backdrop with blur effect */}
        <div
          className={`absolute inset-0 bg-black/70 backdrop-blur-md transition-opacity duration-500 ${
            isMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={(e) => {
            // Make sure we're clicking the backdrop itself, not its children
            if (e.target === e.currentTarget) {
              setIsMenuOpen(false)
            }
          }}
        ></div>

        {/* Menu panel sliding from right */}
        <div
          className={`absolute top-0 right-0 w-full sm:w-80 bg-gradient-to-b from-radio-dark-blue/90 to-black/90 shadow-2xl flex flex-col transition-transform duration-500 ease-out transform ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ backdropFilter: 'blur(16px)' }}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `
                  radial-gradient(circle at 25px 25px, rgba(30, 174, 215, 0.15) 2%, transparent 0%),
                  linear-gradient(to right, rgba(30, 174, 215, 0.05) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(30, 174, 215, 0.05) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px, 25px 25px, 25px 25px',
              }}
            ></div>
            <div className="absolute top-0 right-0 w-full h-full bg-gradient-radial from-radio-blue/5 via-transparent to-transparent opacity-70"></div>
          </div>

          {/* Header with logo and close button */}
          <div className="flex justify-between items-center p-5 border-b border-radio-blue/20 relative z-10">
            <div className="relative w-20 h-[10.5px] -top-3">
              <Image
                src="/assets/img/logo.png"
                alt="Логотип Радио Саня"
                width={80}
                height={42}
                className="object-contain"
              />
            </div>
            <button
              className="p-2 rounded-full bg-black/30 text-white hover:text-radio-blue hover:bg-black/50 transition-all"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="px-5 py-6 flex-grow relative z-10">
            <ul className="space-y-3" role="menubar">
              {[
                {
                  name: 'ГЛАВНАЯ',
                  action: (e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault()
                    scrollToTop()
                  },
                  href: '/',
                },
                {
                  name: 'ПОДДЕРЖКА',
                  action: (e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault()
                    scrollToSection('support')
                  },
                  icon: <Heart className="w-5 h-5" />,
                  href: '/#support',
                },
                {
                  name: 'КОНТАКТЫ',
                  action: (e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault()
                    scrollToSection('contacts')
                  },
                  href: '/#contacts',
                },
                {
                  name: 'СТРИМ НА ЗАКАЗ',
                  href: '/strim-na-zakaz',
                  icon: <Radio className="w-5 h-5" />,
                  action: () => setIsMenuOpen(false),
                },
              ].map((item, index) => (
                <li
                  key={item.name}
                  style={{
                    animationDelay: `${index * 100 + 200}ms`,
                    opacity: isMenuOpen ? 1 : 0,
                    transform: isMenuOpen ? 'translateX(0)' : 'translateX(20px)',
                    transition: 'all 0.4s ease-out',
                  }}
                >
                  <a
                    onClick={item.action}
                    href={item.href}
                    className="group flex items-center justify-between w-full py-3 px-0 font-menu text-xl tracking-wider text-white hover:text-radio-blue transition-colors rounded-lg"
                    role="menuitem"
                  >
                    <div className="flex items-center">
                      {item.icon && <span className="mr-3 text-radio-blue">{item.icon}</span>}
                      {item.name}
                    </div>
                    <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity text-radio-blue transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  )
}
